import cx from "classnames";
import React, { useState, useRef, Fragment, useMemo } from "react";

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  Flex,
  List,
  Text,
  useOnClickOutside,
  isNullish
} from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import {
  NormalizeSearchResultOptions,
  SelectSearchResult,
  GetFieldValueSearchCriteriaText,
  GetFieldDefsFromList
} from "@/Utils/DataLib";
import { GetSearchResults } from "@/Utils/Promises";
import FieldToggleMenu from "../../Menus/FieldToggleMenu/FieldToggleMenu";
import { getHoverTipContent, setObject, getFeatureAccess } from "@/Utils/Misc";
import { GetSessionSearchKeys, UpdateSessionSearchKeys } from "@/Utils/StateLib";
import { FieldInputValuePickerComposite } from "@/appComponents/Blocks/Composites/FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { checkDisconnected } from "../SearchResult/SearchResult";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList, enumFieldType } from "@coreapp/constants";
import { Cappstr } from "@coreapp/utils/Cappstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { filterInternalFields } from "@/Utils/FilterInternalFields";

const defaultAdvSearchKeys = ["PersonID", "Name", "Title", "Division", "Location"];
const ADVSRCH_SRCH_BTN = "advsrch-srch-btn";

export const GetFieldLabelFromFieldName = (fieldName, className = "Box") => {
  const d = StateLib.GetDoc();
  if (!d) return "";
  const objDB = d.GetObjDB();
  const boxClass = objDB.GetClassByName(className);
  let fieldDef = boxClass.GetFieldDefByID(fieldName);
  if (!fieldDef) fieldDef = boxClass.GetFieldDefByName(fieldName);
  if (fieldDef == null) {
    return fieldName;
  }
  let fieldLabel = fieldDef.GetFieldLabel();
  if (!fieldLabel || fieldLabel.length === 0) fieldLabel = fieldDef.GetFieldName();
  return fieldLabel;
};

const GetDefaultSearchKeys = (showAllFieldNames = false) => {
  const doc = StateLib.GetDoc();
  if (!doc) {
    return [];
  }

  const objDB = doc.GetObjDB();
  const boxClass = objDB.GetClassByName("Box");

  const searchKeys = doc.GetSearchKeys();
  const defaultFieldNames = [];

  for (let i of defaultAdvSearchKeys) {
    // Find field defintion for default field
    let fieldDef = boxClass.GetFieldDefByID(i);
    if (!fieldDef) {
      fieldDef = boxClass.GetFieldDefByName(i);
    }

    // Field definition not found for the field
    if (!fieldDef) {
      continue;
    }

    const fieldName = fieldDef.GetFieldName();
    if (showAllFieldNames) {
      defaultFieldNames.push(fieldName);
      continue;
    }

    // Ensure that the field is configured in Search + Directory
    const isConfiguredKey = searchKeys.some(({ keyDef }) => keyDef === fieldName);
    if (!isConfiguredKey) {
      continue;
    }

    defaultFieldNames.push(fieldName);
  }

  return defaultFieldNames.map(fieldName => ({ keyDef: fieldName }));
};

const GetInitialSearchKeys = (showAllFieldNames, sessionKeyContextId) => {
  const doc = StateLib.GetDoc();
  const defaultSearchKeys = GetDefaultSearchKeys(showAllFieldNames);
  if (!doc) {
    return defaultSearchKeys;
  }

  let targetKeys = defaultSearchKeys;

  const userSessionSearchKeys = GetSessionSearchKeys(sessionKeyContextId);
  if (userSessionSearchKeys && userSessionSearchKeys.length > 0) {
    targetKeys = userSessionSearchKeys;
  }

  if (showAllFieldNames) {
    return targetKeys;
  }

  const searchKeys = doc.GetSearchKeys();
  return targetKeys.filter(t => searchKeys.some(({ keyDef }) => keyDef === t["keyDef"]));
};

const NormalizeCriteriaValue = (boxClass, fieldName, value, isRemoteQuery) => {
  if (isRemoteQuery) {
    if (value === "") {
      return { $eq: "" };
    }

    const fieldDef = boxClass?.GetFieldDefByName(fieldName);
    const isReadOnly = fieldDef?.FieldReadOnly();
    const isNumberType = fieldDef?.GetFieldType() === enumFieldType.number;
    if (isReadOnly && isNumberType) {
      return { $match: value };
    }
  }

  return value;
};

const BuildQueryObject = (doc, searchCriteria) => {
  const queryObj = {};

  const isRemoteQuery = doc?.IsServerChart();
  const objDB = doc?.GetObjDB();
  const boxClass = objDB?.GetClassByName("Box");

  for (let key in searchCriteria) {
    if (isNullish(searchCriteria[key])) {
      continue;
    }

    queryObj[key] = NormalizeCriteriaValue(boxClass, key, searchCriteria[key], isRemoteQuery);
  }

  return queryObj;
};

const SearchCriteriaMenu = ({
  selectedFieldNames,
  showAllFieldNames,
  setSearchCriteria,
  popoverRef,
  onResetFields
}) => {
  const fieldOpenCtrlRef = useRef(null);

  const fieldNames = useMemo(() => {
    const doc = StateLib.GetDoc();
    let workingFieldNames = [];

    if (showAllFieldNames) {
      const fields = doc.GetFieldList2(true, false, false, false);
      workingFieldNames = fields.map(item => item.GetFieldName());
    } else {
      const searchKeys = doc.GetSearchKeys();
      workingFieldNames = searchKeys.map(({ keyDef }) => keyDef);
    }

    const filteredFieldNames = filterInternalFields(workingFieldNames);
    return filteredFieldNames.sortBy(true, null, o => GetFieldLabelFromFieldName(o));
  }, [showAllFieldNames]);

  const RemoveSearchCriteriaKey = field => {
    setSearchCriteria(prevState => {
      const newState = { ...prevState };
      delete newState[field];
      return newState;
    });
  };

  const AddSearchCriteriaKey = field => {
    setSearchCriteria(prevState => {
      const newState = { ...prevState };
      newState[field] = null;
      return newState;
    });
  };

  const ToggleField = target => {
    const targetField = target.value;

    const wasSelected = selectedFieldNames.includes(targetField);
    if (wasSelected) {
      RemoveSearchCriteriaKey(targetField);
    } else {
      AddSearchCriteriaKey(targetField);
    }

    const wsMgr = StateLib.GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("chart_mode.advanced_search.saved", {
      field: targetField,
      enabled: !wasSelected
    });
  };

  const fieldTrigger = (
    <Button
      dataTestId={`${ADVSRCH_SRCH_BTN}-fields-trigger`}
      modifier="secondary"
      type="button"
      size={"small"}
      iconProps={{ name: "gear" }}
      iconAlignEnd={false}
      tooltipProps={{
        ...getHoverTipContent("Advanced Search Fields", "bottom-start"),
        referenceOpenCtrlRef: fieldOpenCtrlRef
      }}
      disabled={!getFeatureAccess(enumFeaturesList.advancedSearch)}
    >
      {appstr("Fields")}
    </Button>
  );

  const defaultSearchKeys = GetDefaultSearchKeys();
  const isDefaultFields =
    defaultSearchKeys.length === selectedFieldNames.length &&
    defaultSearchKeys.every(({ keyDef }) => selectedFieldNames.includes(keyDef));

  return (
    <span style={{ flex: "0 0 18%", display: "flex", justifyContent: "flex-end" }}>
      <FieldToggleMenu
        dataTestIdPrefix="advanced-search-fields-toggle-"
        small={true}
        selectedFieldNames={selectedFieldNames}
        openCtrlRef={fieldOpenCtrlRef}
        fieldNames={fieldNames}
        onChange={ToggleField}
        trigger={fieldTrigger}
        listHeader={{
          label: appstr("Show search fields"),
          postSearchNode: (
            <Flex width={"100%"} justifyContent={"end"} padding={"4px 8px"}>
              <Button
                dataTestId={`${ADVSRCH_SRCH_BTN}-fields-reset`}
                modifier={"secondary"}
                type={"button"}
                size={"small"}
                disabled={isDefaultFields}
                onClick={onResetFields}
              >
                {appstr("Reset")}
              </Button>
            </Flex>
          )
        }}
        popoverProps={{
          floatingProps: {
            ref: popoverRef
          }
        }}
        listProps={{
          optionSize: "small",
          listWidth: "large"
        }}
        optionSize={"small"}
        position="bottom-end"
        stayAlive={true}
        fillWidth={false}
      />
    </span>
  );
};

const AdvancedSearchModal = ({
  show,
  onClose,
  displayResultsInContainer = true,
  onSearchResult,
  processFullResult,
  projectFields,
  lastSearchCriteria,
  sessionKeyContextId,
  showAllFieldNames,
  queryOptions = {},
  useProjectFieldDefs = false
}) => {
  const { highlightRecord, handleSideBarModeChange } = useRightSidebarContext();
  const baseClass = "ocn-advanced-search-modal-wrapper";
  const modalRef = useRef(null);
  const [queryResult, setQueryResult] = useState({});
  const [docResults, setDocResults] = useState([]);
  const [displayResults, setDisplayResults] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(() => {
    if (lastSearchCriteria) {
      return lastSearchCriteria;
    }

    const searchKeys = GetInitialSearchKeys(showAllFieldNames, sessionKeyContextId);
    const workingCriteria = {};
    searchKeys.forEach(item => {
      workingCriteria[item.keyDef] = null;
    });
    return workingCriteria;
  });

  const projectFieldNames = GetFieldDefsFromList(projectFields, useProjectFieldDefs).map(item =>
    item.GetFieldName()
  );

  let doc = StateLib.GetDoc();

  if (doc?.IsPlanChart()) {
    const wsMgr = StateLib.GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    queryOptions.planID = planMgr?.GetPlanID();
  }

  const hasDirtyState = Object.values(searchCriteria).some(item => !isNullish(item));

  const DoDocSearch = () => {
    const doc = StateLib.GetDoc();
    const queryObject = BuildQueryObject(doc, searchCriteria);
    let dbObjMatches = [];
    if (doc) doc.DoAdvStaticChartSearch(queryObject, dbObjMatches);
    setDocResults(dbObjMatches.slice(0));
    FinishSearch(dbObjMatches);
  };

  const DoQuerySearch = async () => {
    const doc = StateLib.GetDoc();
    const queryObject = BuildQueryObject(doc, searchCriteria);
    if (!queryObject) return;
    const queryResult = await GetSearchResults(queryObject, {
      projectFields: projectFieldNames,
      ...queryOptions
    });
    setQueryResult(queryResult.result);
    if (processFullResult) {
      FinishSearch(queryResult.result);
    } else {
      FinishSearch(queryResult.result ? queryResult.result.results : []);
    }
  };

  const FinishSearch = result => {
    if (displayResultsInContainer) {
      setDisplayResults(true);
    } else {
      onSearchResult(result, searchCriteria);
      onClose();
    }
  };

  const handleSearch = () => {
    doc.IsServerChart() ? DoQuerySearch() : DoDocSearch();
  };

  const handleResetSearch = () => {
    setSearchCriteria(prevState => setObject(prevState, null));
    if (displayResultsInContainer) {
      setDisplayResults(false);
    }
  };

  const handleCriteriaKeyChange = updater => {
    setSearchCriteria(prevState => {
      const nextState = updater(prevState);
      UpdateSessionSearchKeys(nextState, sessionKeyContextId);
      return nextState;
    });
  };

  const handleResetFields = () => {
    handleCriteriaKeyChange(prevState => {
      const searchKeys = GetDefaultSearchKeys();
      return Object.fromEntries(
        searchKeys.map(({ keyDef }) => [keyDef, prevState[keyDef] ?? null])
      );
    });
  };

  const toggleDisplayResults = () => {
    setDisplayResults(!displayResults);
  };

  const clickResult = option => {
    // TODO: Might want to change this once Position adv. search is implemented.
    const isHolder = doc.IsPositionDocument();

    if (!isHolder && !doc.IsServerChart() && checkDisconnected(option.result)) {
      handleSideBarModeChange("staging");
      highlightRecord(option.result);
      onClose();
      return;
    }

    SelectSearchResult(option, isHolder);
    onClose();
  };

  const fieldPopoverRef = useRef(null);
  const valuePopoverRef = useRef(null);

  useOnClickOutside([modalRef, fieldPopoverRef, valuePopoverRef], () => {
    onClose();
  });

  const GetCurrentSearchResults = () => {
    let doc = StateLib.GetDoc();
    let searchResults = doc.IsServerChart() ? queryResult.results : docResults;
    if (!searchResults) searchResults = [];
    return searchResults;
  };

  const searchResults = GetCurrentSearchResults();
  const handleSetCriteriaValue = (data, isFieldValuePick = false) => {
    setSearchCriteria(prevState => {
      let value = data.value;

      // If a user is not explicitly setting to an empty string, we should set the value to null
      if (data.value === "" && !isFieldValuePick) {
        value = null;
      }

      return { ...prevState, [data.field]: value };
    });
  };

  return (
    <div className={baseClass} style={{ position: "relative" }}>
      <Modal
        ref={modalRef}
        id="advanced-search-modal"
        show={show}
        onClose={onClose}
        minWidth={"medium"}
        hideOverlay={true}
        withShadow={true}
        centerScreen={false}
        customStyle={{ top: 4, transform: "translate(-50%)" }}
      >
        <DialogTitle divider={true} onClose={onClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"search"} />
            {appstr("Advanced search")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"ocn-advanced-search-content"}>
          <Flex gap={displayResults ? "8px" : "12px"} direction={"column"}>
            {displayResults ? (
              <Fragment>
                <Text size={"small"} colorModifier="lightgrey">
                  {GetFieldValueSearchCriteriaText(searchCriteria)}
                </Text>
                <Button
                  iconProps={{
                    name: "caret-left"
                  }}
                  dataTestId={"advsrch-hide-results"}
                  modifier={"secondary"}
                  onClick={toggleDisplayResults}
                >
                  {appstr("Refine your search")}
                </Button>
                <Text size={"small"} colorModifier="lightgrey">
                  {"Displaying " + searchResults.length + " matches."}
                </Text>
                <div className={cx("ocn-advanced-search-results", "ocn-list-scrollbar")}>
                  <List
                    enableVirtualization={false}
                    listWidth="fill"
                    options={NormalizeSearchResultOptions(
                      searchResults,
                      clickResult,
                      "advsrch-srch-result",
                      undefined,
                      doc?.IsServerChart(),
                      undefined,
                      false,
                      null,
                      false,
                      null,
                      false,
                      doc
                    )}
                    coloredSelection={true}
                  />
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <Flex justifyContent={"space-between"} alignItems={"center"}>
                  <Text size="small">
                    {appstr(
                      "Customize your search with various criteria to find the perfect match"
                    )}
                  </Text>
                  {!isNullish(doc) && (
                    <SearchCriteriaMenu
                      showAllFieldNames={showAllFieldNames}
                      onResetFields={handleResetFields}
                      selectedFieldNames={Object.keys(searchCriteria)}
                      setSearchCriteria={handleCriteriaKeyChange}
                      popoverRef={fieldPopoverRef}
                    />
                  )}
                </Flex>
                <div className={cx("ocn-advanced-search-results", "ocn-list-scrollbar")}>
                  <Flex direction={"column"} gap={"16px"}>
                    {Object.entries(searchCriteria).map(([field, value]) => {
                      const inputProps = {
                        clearable: value?.length > 0
                      };

                      if (value === "") {
                        inputProps.placeholder = `(${appstr("Blank")})`;
                        inputProps.clearable = true;
                      }
                      return (
                        <FieldInputValuePickerComposite
                          ignorePicklistValues
                          allowBlockedValueOption
                          key={field}
                          field={field}
                          value={value}
                          onChange={handleSetCriteriaValue}
                          popoverRef={valuePopoverRef}
                          inputProps={inputProps}
                          dataTestIdPrefix={"advsrch-field-"}
                        />
                      );
                    })}
                  </Flex>
                </div>
              </Fragment>
            )}
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Secondary
            dataTestId={`${ADVSRCH_SRCH_BTN}-reset`}
            disabled={!hasDirtyState}
            onClick={() => handleResetSearch()}
          >
            {appstr("Reset")}
          </DialogActions.Secondary>
          <DialogActions.Primary dataTestId={ADVSRCH_SRCH_BTN} onClick={() => handleSearch()}>
            {appstr("Search")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </div>
  );
};

export default AdvancedSearchModal;
